import {
  getV1CompaniesCompanyIdProjectsProjectIdAutoAddOnAssignments,
  RateOwnerType,
} from '@treadinc/horizon-api-spec';
import { useEffect } from 'react';

import { AddOnAssignment } from '~hooks/useAddOns/models';
import type { Order } from '~hooks/useOrders/models';

import type { OrderFormV3FieldValues } from '../OrderFormV3Context';
import { loadAutoAddOnAssignments } from './loadAutoAddOnAssignments';

/**
 * Triggers: `project.id` (watched, via the Provider) and the order identity.
 * Writes:   the Provider's `project{Customer,Vendor,Driver}AddOnAssignments`
 *           state — the project-level auto add-on cascade FreightRates
 *           consumes.
 * Guards:   NEW orders only (`!defaultOrder?.id`) with a project selected —
 *           persisted orders keep the add-ons saved on the order.
 * Legacy:   the legacy form's project add-on effect and fetch helper.
 * Test:     __tests__/OrderFormV3Context.test.tsx ("project add-on fetches").
 */

/** Fetches the project-level auto add-ons for one rate owner type. */
async function fetchProjectAddOnAssignments(
  companyId: string,
  projectId: string,
  rateOwnerType: RateOwnerType,
): Promise<AddOnAssignment[]> {
  const response = await getV1CompaniesCompanyIdProjectsProjectIdAutoAddOnAssignments({
    path: { 'company-id': companyId, 'project-id': projectId },
    query: {
      'filter[account_type]':
        rateOwnerType !== RateOwnerType.DRIVER ? rateOwnerType : undefined,
    },
  });

  return response.data.data.map((addOnAssignment) =>
    AddOnAssignment.parse(addOnAssignment),
  );
}

export function useProjectAddOnFetch({
  companyId,
  watchedProject,
  defaultOrder,
  setProjectCustomerAddOnAssignments,
  setProjectVendorAddOnAssignments,
  setProjectDriverAddOnAssignments,
}: {
  companyId: string;
  watchedProject: OrderFormV3FieldValues['project'] | undefined;
  defaultOrder: Order | null;
  setProjectCustomerAddOnAssignments: (a: AddOnAssignment[] | undefined) => void;
  setProjectVendorAddOnAssignments: (a: AddOnAssignment[] | undefined) => void;
  setProjectDriverAddOnAssignments: (a: AddOnAssignment[] | undefined) => void;
}): void {
  useEffect(() => {
    if (!defaultOrder?.id && watchedProject?.id) {
      const projectId = watchedProject.id;
      const setters = [
        [RateOwnerType.CUSTOMER, setProjectCustomerAddOnAssignments],
        [RateOwnerType.VENDOR, setProjectVendorAddOnAssignments],
        [RateOwnerType.DRIVER, setProjectDriverAddOnAssignments],
      ] as const;
      for (const [rateOwnerType, apply] of setters) {
        void loadAutoAddOnAssignments(
          () => fetchProjectAddOnAssignments(companyId, projectId, rateOwnerType),
          apply,
        );
      }
    }
    // The setters are read via closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, defaultOrder?.id, watchedProject?.id]);
}
